// ENI-167 — Cliente de las imágenes contra el backend.
//
// Antes cada pantalla hablaba directo con Firebase Storage (`firebase.storage().ref(...).put()`),
// sin autenticarse contra Firebase: la sesión del backoffice es del backend, no de Firebase. Para
// que eso funcionara, las reglas del bucket tenían que conceder `write` a cualquiera — y ese
// "cualquiera" incluye a todo internet, que podía pisar o borrar la foto de cualquier producto.
//
// Ahora las escrituras van por el backend, que entra al bucket con la service account. La
// LECTURA no cambia: las imágenes se siguen bajando de `storage.googleapis.com` directo, que es
// como las lee la tienda. Por eso el backend devuelve la `url` de cada objeto y acá sólo se hace
// `fetch` de esa URL.

import { endSession } from './session';

const config = process.env;

const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;

const apiUrl = (path) => apiBase() + path;

const authHeaders = () => ({ 'access-token': localStorage.getItem('token') });

// Sigue el molde de `mlApi.js` / `collectionsApi.js`: thin wrappers sobre fetch en un solo
// archivo. En particular el manejo del 1002, que es fácil de perder: sin `endSession()` el admin
// se queda en una pantalla que ya no puede guardar nada, viendo "La sesión expiró" una y otra vez
// sin que nadie lo mande al login.
//
// (Ésta es la cuarta copia del mismo `request` en el repo — reservationsApi, collectionsApi,
// mlApi y ésta. Extraerlo a un `utils/apiClient.js` es un follow-up que toca esos tres archivos y
// queda fuera del alcance de ENI-167.)
async function request(path, options = {}) {
    let res;
    try {
        res = await fetch(apiUrl(path), {
            ...options,
            headers: { ...authHeaders(), ...(options.headers || {}) },
        });
    } catch (networkError) {
        const err = new Error('No pudimos hablar con el servidor. Revisá la conexión y reintentá.');
        err.code = 0;
        throw err;
    }

    let body = null;
    try {
        body = await res.json();
    } catch (parseError) {
        body = null;
    }

    const code = body && Number(body.error) > 0 ? Number(body.error) : 0;

    if (code === 1002) {
        endSession();
        const err = new Error((body && body.message) || 'La sesión expiró');
        err.code = 1002;
        throw err;
    }

    if (!res.ok || code) {
        const err = new Error((body && body.message) || 'No se pudo completar la operación con las imágenes');
        err.code = code;
        throw err;
    }

    return body ? body.data : null;
}

const get = (path) => request(path, { method: 'GET' });

const del = (path) => request(path, { method: 'DELETE' });

// Sube un Blob/File. Va como multipart: el backend valida tamaño y magic bytes antes de escribir.
// OJO: no se setea 'Content-Type' — con un body `FormData` lo tiene que poner el navegador, que
// es el único que conoce el `boundary`.
function upload(path, blob, filename) {
    const form = new FormData();
    form.append('image', blob, filename || 'imagen');
    return request(path, { method: 'POST', body: form });
}

// ── Producto / accesorio / usado ─────────────────────────────────────────────────────────

// `folder` es el nombre de carpeta del bucket, sin barra: 'products' | 'accessories' | 'used'.
// Lo arma `getProductImageFolder` de utils/productForm.js, que devuelve '/products/123'.
export function splitProductFolder(folder) {
    const [carpeta, code] = String(folder || '').replace(/^\//, '').split('/');
    return { carpeta, code };
}

// Devuelve [{position, path, url, size, contentType}] de las imágenes que existen.
export function listProductImages(folder) {
    const { carpeta, code } = splitProductFolder(folder);
    return get(`image/product/${carpeta}/${code}`);
}

export function uploadProductImage(folder, position, blob) {
    const { carpeta, code } = splitProductFolder(folder);
    return upload(`image/product/${carpeta}/${code}/${encodeURIComponent(position)}`, blob, String(position));
}

// Vacía la carpeta del producto. Es destructivo a propósito y por eso el flujo de edición lo
// llama SIEMPRE seguido de la subida.
export function clearProductFolder(folder) {
    const { carpeta, code } = splitProductFolder(folder);
    return del(`image/product/${carpeta}/${code}`);
}

// ── Portada ──────────────────────────────────────────────────────────────────────────────

// Estado de los 12 objetos (4 tamaños × 3 slides), exista el archivo o no.
export function getPortadaState() {
    return get('image/portada');
}

export function uploadPortada(size, slide, blob) {
    return upload(`image/portada/${size}/${encodeURIComponent(slide)}`, blob, slide);
}

// ── Póster de evento ─────────────────────────────────────────────────────────────────────

// El nombre del objeto lo elige el backend. Devuelve {path, url}: la `url` es lo que se guarda
// en `event.poster_url`.
export function uploadEventPoster(blob, filename) {
    return upload('image/event/poster', blob, filename);
}

// ── Adjuntos de compra ───────────────────────────────────────────────────────────────────

export function listPurchaseFiles(purchaseId) {
    return get(`image/purchase/${purchaseId}`);
}

// ── Foto de perfil ───────────────────────────────────────────────────────────────────────

// La propia: el backend toma el email de la sesión. Antes el email viajaba desde el cliente, así
// que con sólo saber una dirección se podía pisar la foto de cualquier admin.
export function getOwnProfileImage() {
    return get('image/profile');
}

export function uploadOwnProfileImage(blob) {
    return upload('image/profile', blob, 'perfil');
}

export function deleteOwnProfileImage() {
    return del('image/profile');
}

// La de otro admin: sólo en el alta (rol 1).
export function uploadProfileImageFor(email, blob) {
    return upload(`image/profile/${encodeURIComponent(email)}`, blob, 'perfil');
}

// ── Utilidades de lectura ────────────────────────────────────────────────────────────────

// Baja una imagen y la devuelve como data URL, que es el formato con el que trabajan los pickers
// y los forms.
//
// Recibe el `contentPath` que viene en el listado (una ruta de NUESTRA API), no la `url` pública
// del bucket. Un `<img src>` contra el bucket anda sin más, pero un `fetch` necesita CORS, y el
// CORS es config por bucket: al 2026-08-08 prod y test la tenían cargada y staging no. Con la URL
// pública, la pantalla de edición perdía las imágenes en staging sin mostrar ningún error.
export function fetchAsDataUrl(contentPath) {
    // No pasa por `request()` porque la respuesta feliz son BYTES, no JSON. El manejo de error
    // sí se replica, incluido `endSession()` en el 1002.
    return fetch(apiUrl(contentPath), { headers: authHeaders() })
        .then((res) => {
            if (!res.ok) throw new Error(`No se pudo bajar la imagen (${res.status})`);
            // El backend responde los errores con HTTP 200 y `{error, message}` en el cuerpo
            // (convención del repo), así que `res.ok` no alcanza: sin este chequeo, un 1002 por
            // sesión vencida se convertiría en un data URL con JSON adentro y la pantalla
            // mostraría una imagen rota sin decir por qué.
            const tipo = res.headers.get('content-type') || '';
            if (tipo.includes('application/json')) {
                return res.json().then((body) => {
                    if (Number(body.error) === 1002) endSession();
                    const err = new Error(body.message || 'No se pudo bajar la imagen');
                    err.code = Number(body.error) || 0;
                    throw err;
                });
            }
            return res.blob();
        })
        .then((blob) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        }));
}

// Un data URL a Blob, para poder subirlo. Los pickers guardan las imágenes como data URL.
export function dataUrlToBlob(dataUrl) {
    return fetch(dataUrl).then((res) => res.blob());
}
